import { useMemo, useState } from "react";
import Image from "next/image";
import classNames from "classnames";

import MaskedInput, { Mask } from "react-text-mask";
import classes from "./TextInput.module.css";
import OpenEyeIcon from "static/icons/open_eye.svg";
import ClosedEyeIcon from "static/icons/closed_eye.svg";
import ClearIcon from "static/icons/clear.png";
import useTranslation from "next-translate/useTranslation";

type TextInputVariantKey = "default" | "phone-prefix";

type TextInputProps = {
  id?: string;
  label?: string;
  value: string;
  onChangeHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  onBlurHandler?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  onClearHandler?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  type?: string;
  variant?: TextInputVariantKey;
  pattern?: string;
  rightAddon?: React.ReactNode;
  maxCharacters?: number;
  disabled?: boolean;
};

export const TextInput = ({
  id,
  name,
  label,
  value,
  onChangeHandler,
  onBlurHandler,
  placeholder,
  required,
  error,
  helperText,
  className,
  onClearHandler,
  type,
  variant,
  pattern,
  rightAddon,
  maxCharacters,
  disabled,
}: TextInputProps) => {
  const [letterCount, setLetterCount] = useState(0);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (maxCharacters) {
      if (event.target.value.length <= maxCharacters) {
        setLetterCount(event.target.value.length);
        onChangeHandler(event);
      }
    } else {
      onChangeHandler(event);
    }
  };

  const Input = useMemo(() => {
    return (
      <input
        type={type || "text"}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={onBlurHandler}
        pattern={pattern}
        disabled={disabled}
      />
    );
  }, [
    id,
    type,
    name,
    placeholder,
    value,
    onBlurHandler,
    pattern,
    disabled,
    handleChange,
  ]);

  const InputComponent = useMemo(() => {
    switch (variant) {
      case "phone-prefix":
        return (
          <div className={classes.phonePrefixSection}>
            <div className={classes.phonePrefixPlus}>
              <p>+</p>
            </div>
            {Input}
          </div>
        );
      default:
        return Input;
    }
  }, [variant, Input]);

  return (
    <div
      className={classNames(
        classes.textInputField,
        {
          [classes.error]: error,
          [classes.rightAddonInput]: rightAddon || onClearHandler,
          [classes.disabled]: disabled,
        },
        className
      )}
    >
      {label && (
        <label htmlFor={id}>
          {label}
          {required && <span> *</span>}
        </label>
      )}
      {InputComponent}
      {rightAddon && <div className={classes.rightAddon}>{rightAddon}</div>}
      {value && onClearHandler && (
        <button
          name={name}
          onClick={onClearHandler}
          className={classes.textInputClearButton}
        >
          <Image src={ClearIcon} alt={"clear"} width={24} height={24} />
        </button>
      )}
      <div className={classes.infoContainer}>
        {maxCharacters && (
          <p className={classes.charactersCountText}>
            {letterCount}/{maxCharacters}
          </p>
        )}
        {error && <p>{helperText}</p>}
      </div>
    </div>
  );
};

type CustomMaskedTextInputProps = TextInputProps & {
  mask: Mask;
};

export const CustomMaskedTextInput = ({
  id,
  name,
  label,
  value,
  onChangeHandler,
  onBlurHandler,
  placeholder,
  required,
  error,
  helperText,
  mask,
  disabled,
  className,
}: CustomMaskedTextInputProps) => {
  const { t } = useTranslation("common");

  return (
    <div
      className={classNames(
        classes.textInputField,
        classes.maskedTextInput,
        error && classes.error,
        className
      )}
    >
      {label && (
        <label htmlFor={id}>
          {t(label)}
          {required && <span> *</span>}
        </label>
      )}

      <MaskedInput
        id={id}
        name={name}
        value={value}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        placeholder={t(placeholder!)}
        mask={mask}
        disabled={disabled}
      />
      {error && <p>{t(helperText!)}</p>}
    </div>
  );
};

export const PasswordInput = ({
  id,
  name,
  label,
  value,
  onChangeHandler,
  onBlurHandler,
  placeholder,
  required,
  error,
  helperText,
  className,
}: TextInputProps) => {
  const [passwordShown, setPasswordShown] = useState(false);
  const { t } = useTranslation("common");

  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  return (
    <div
      className={classNames(
        classes.textInputField,
        classes.passwordInput,
        error && classes.error,
        className
      )}
    >
      <label htmlFor={id}>
        {t(label!)}
        {required && <span> *</span>}
      </label>
      <input
        id={id}
        type={passwordShown ? "text" : "password"}
        name={name}
        placeholder={t(placeholder!)}
        value={value}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
      />
      <span
        onClick={togglePassword}
        className={classes.passwordInputVisibilityIcon}
      >
        <Image
          src={passwordShown ? ClosedEyeIcon : OpenEyeIcon}
          alt={t("show_password")}
          width={24}
          height={24}
        />
      </span>
      {error && <p>{t(helperText!)}</p>}
    </div>
  );
};
